import { Attribution, SortBy, SortType } from 'types'

import { buildAdsTableFilterSql } from './adsTableFilters'

interface Params {
  endDate: string
  startDate: string
  limit: number
  offset: number
  attr: Attribution
  id: string
  sortBy?: SortBy
  sortType?: SortType
  groupBy: 'campaign' | 'ad' | 'ad_set'
  /** Drill-down: limit to one Meta campaign id */
  filterCampaignId?: string | null
  /** Drill-down: limit to one ad set id (only when groupBy === 'ad') */
  filterAdSetId?: string | null
}

export const getAdsTableData = ({
  endDate,
  startDate,
  limit,
  offset,
  attr,
  id,
  sortBy,
  sortType = 'DESC',
  groupBy,
  filterCampaignId,
  filterAdSetId,
}: Params) => {
  const attrTableName =
    attr === 'first' || attr === 'last' ? attr : `${attr}_source`
  let sortParamName

  if (sortBy === 'views') {
    sortParamName = `stats.${sortBy}`
  } else {
    sortParamName = `${attrTableName}.${sortBy}`
  }

  const sorting = sortBy ? `ORDER BY ${sortParamName} ${sortType}` : ''

  const attrPrefix = attr === 'first' || attr === 'last' ? `${attr}_` : ''

  let fetchingProps
  if (groupBy === 'campaign') {
    fetchingProps = `${attrPrefix}ad_campaign.ad_campaign_name, ${attrPrefix}ad_campaign.ad_campaign_id, ${attrPrefix}ad_campaign.ad_campaign_ext_id`
  } else if (groupBy === 'ad') {
    fetchingProps = `${attrPrefix}ad_source.source_id, ${attrPrefix}ad_source.ad_ext_id, ${attrPrefix}ad_source.ad_name, ${attrPrefix}ad_set.ad_set_name, ${attrPrefix}ad_set.ad_set_ext_id, ${attrPrefix}ad_set.ad_campaign_id, ${attrPrefix}ad_campaign.ad_campaign_name, ${attrPrefix}ad_campaign.ad_campaign_ext_id`
  } else if (groupBy === 'ad_set') {
    fetchingProps = `${attrPrefix}ad_set.ad_set_id, ${attrPrefix}ad_set.ad_set_name, ${attrPrefix}ad_set.ad_set_ext_id, ${attrPrefix}ad_set.ad_campaign_id, ${attrPrefix}ad_campaign.ad_campaign_name, ${attrPrefix}ad_campaign.ad_campaign_ext_id`
  }

  const filterSql = buildAdsTableFilterSql(attr, groupBy, filterCampaignId, filterAdSetId)

  const whereClause = `WHERE stats.event_date <= '${endDate}' AND stats.event_date >= '${startDate}' AND stats.channel_id = '${id}'${filterSql}`
  const groupLimit = `GROUP BY ${fetchingProps} ${sorting} LIMIT ${limit} OFFSET ${offset}`

  switch (attr) {
    case 'first':
      return `SELECT SUM(stats.views), SUM(stats.new_visitors), SUM(first.new_customers), SUM(first.leads), SUM(first.form_submits), SUM(first.total_orders), SUM(first.total_amount), SUM(first.recurring_payments_amount), SUM(first.orders_amount), SUM(first.ad_impressions), SUM(first.ad_spend), SUM(first.platform_leads), SUM(first.platform_purchases), SUM(first.platform_weberlo_leads), SUM(first.platform_weberlo_purchases), SUM(first.platform_purchase_value), SUM(first.platform_weberlo_purchase_value), ${fetchingProps} FROM source ${whereClause} ${groupLimit}`
    case 'last':
      return `SELECT SUM(stats.views), SUM(stats.new_visitors), SUM(last.new_customers), SUM(last.leads), SUM(last.form_submits), SUM(last.total_orders), SUM(last.total_amount), SUM(last.recurring_payments_amount), SUM(last.orders_amount), SUM(last.ad_impressions), SUM(last.ad_spend), SUM(last.platform_leads), SUM(last.platform_purchases), SUM(last.platform_weberlo_leads), SUM(last.platform_weberlo_purchases), SUM(last.platform_purchase_value), SUM(last.platform_weberlo_purchase_value), ${fetchingProps} FROM source ${whereClause} ${groupLimit}`
    case 'linear':
      return `SELECT SUM(stats.views), SUM(stats.new_visitors), SUM(stats.new_customers), SUM(linear_source.leads), SUM(linear_source.form_submits), SUM(linear_source.total_orders), SUM(linear_source.total_amount), SUM(linear_source.recurring_payments_amount), SUM(linear_source.orders_amount), SUM(linear_source.ad_impressions), SUM(linear_source.ad_spend), SUM(linear_source.platform_leads), SUM(linear_source.platform_purchases), SUM(linear_source.platform_weberlo_leads), SUM(linear_source.platform_weberlo_purchases), SUM(linear_source.platform_purchase_value), SUM(linear_source.platform_weberlo_purchase_value), ${fetchingProps} FROM source ${whereClause} ${groupLimit}`
    case 'ai':
      return `SELECT SUM(stats.views), SUM(stats.new_visitors), SUM(stats.new_customers), SUM(ai_source.leads), SUM(ai_source.form_submits), SUM(ai_source.total_orders), SUM(ai_source.total_amount), SUM(ai_source.recurring_payments_amount), SUM(ai_source.orders_amount), SUM(ai_source.ad_impressions), SUM(ai_source.ad_spend), SUM(ai_source.platform_leads), SUM(ai_source.platform_purchases), SUM(ai_source.platform_weberlo_leads), SUM(ai_source.platform_weberlo_purchases), SUM(ai_source.platform_purchase_value), SUM(ai_source.platform_weberlo_purchase_value), ${fetchingProps} FROM source ${whereClause} ${groupLimit}`
    default:
      return ''
  }
}
